import React, { useState, useRef, useEffect } from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import Input from "components/ui/Input";
import { supabase } from "../../../lib/supabase";
import { getSystemSetting } from "../../../lib/emailService";

const ALLOWED_TYPES = [".zip", ".exe", ".msi", ".pkg", ".tar.gz", ".deb", ".rpm", ".apk"];
const MAX_SIZE_MB = 500;

const isAllowedFile = (file) => {
  const name = file?.name?.toLowerCase() || "";
  return ALLOWED_TYPES?.some((ext) => name?.endsWith(ext));
};

const UploadModal = ({ onClose, onUpload }) => {
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadError, setUploadError] = useState("");
  const [form, setForm] = useState({ name: "", version: "", releaseNotes: "", expirationHours: "24", downloadLimit: "0" });
  const [errors, setErrors] = useState({});
  const fileRef = useRef();

  useEffect(() => {
    // Load defaults from system settings
    Promise.all([
      getSystemSetting('signed_url_ttl_hours'),
      getSystemSetting('signed_url_download_limit'),
    ])?.then(([ttl, limit]) => {
      setForm((prev) => ({
        ...prev,
        expirationHours: ttl || "24",
        downloadLimit: limit || "0",
      }));
    });
  }, []);

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragOver(false);
    const dropped = e?.dataTransfer?.files?.[0];
    if (dropped) validateAndSetFile(dropped);
  };

  const handleFileChange = (e) => {
    if (e?.target?.files?.[0]) validateAndSetFile(e?.target?.files?.[0]);
  };

  const validateAndSetFile = (f) => {
    setUploadError("");
    if (!isAllowedFile(f)) {
      setUploadError(`Tipo de archivo no permitido. Tipos aceptados: ${ALLOWED_TYPES?.join(", ")}`);
      return;
    }
    if (f?.size > MAX_SIZE_MB * 1024 * 1024) {
      setUploadError(`El archivo supera el límite de ${MAX_SIZE_MB} MB.`);
      return;
    }
    setFile(f);
  };

  const validate = () => {
    const errs = {};
    if (!form?.name?.trim()) errs.name = "El nombre es obligatorio";
    if (!form?.version?.trim()) errs.version = "La versión es obligatoria";
    if (!file) errs.file = "Seleccione un archivo";
    if (!form?.expirationHours || isNaN(form?.expirationHours) || Number(form?.expirationHours) < 1)
      errs.expirationHours = "Ingrese horas válidas (mínimo 1)";
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs)?.length) { setErrors(errs); return; }
    setUploading(true);
    setProgress(0);
    setUploadError("");

    try {
      // Simulate progress start
      setProgress(10);

      // Upload to Supabase Storage
      const storagePath = `updates/${Date.now()}_${file?.name}`;
      const { error: storageError } = await supabase?.storage
        ?.from('updates')
        ?.upload(storagePath, file, { upsert: false, cacheControl: '3600' });

      if (storageError) {
        throw new Error(storageError?.message || 'Error al subir el archivo al almacenamiento');
      }

      setProgress(80);

      // Pass data to parent handler
      await onUpload({
        name: form?.name,
        version: form?.version,
        description: form?.releaseNotes,
        expirationHours: form?.expirationHours,
        downloadLimit: form?.downloadLimit,
        file,
        storagePath,
      });

      setProgress(100);
    } catch (err) {
      const msg = err?.message?.includes('row-level security')
        ? 'Sin permisos para subir archivos. Verifique su rol.' : err?.message?.includes('Payload too large') || err?.message?.includes('size')
        ? `El archivo supera el límite de ${MAX_SIZE_MB} MB.`
        : err?.message || 'Error de red al subir el archivo.';
      setUploadError(msg);
      setUploading(false);
      setProgress(0);
    }
  };

  const fileSizeMB = file ? (file?.size / 1024 / 1024)?.toFixed(2) : 0;
  const estimatedSeconds = file && progress > 0 && progress < 100
    ? Math.ceil(((100 - progress) / 100) * (file?.size / 1024 / 1024) * 0.5)
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }}>
      <div className="w-full max-w-lg rounded-xl shadow-xl overflow-hidden" style={{ background: "var(--color-card)" }}>
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: "var(--color-border)" }}>
          <h3 className="font-heading font-bold text-lg" style={{ color: "var(--color-foreground)" }}>Cargar Nueva Actualización</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 transition-base" disabled={uploading}>
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto scrollbar-thin">
          {/* Drop zone */}
          <div
            onDragOver={(e) => { e?.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => !uploading && fileRef?.current?.click()}
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-base ${uploading ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'} ${dragOver ? 'border-teal-500 bg-teal-50' : 'border-gray-300 hover:border-teal-400 hover:bg-gray-50'}`}
            style={dragOver ? { borderColor: "var(--color-primary)", backgroundColor: "rgba(15,118,110,0.05)" } : {}}
          >
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              onChange={handleFileChange}
              accept=".zip,.exe,.msi,.pkg,.tar.gz,.deb,.rpm,.apk"
              disabled={uploading}
            />
            <Icon name="UploadCloud" size={32} color={dragOver ? "var(--color-primary)" : "var(--color-muted-foreground)"} className="mx-auto mb-2" />
            {file ? (
              <div>
                <p className="font-caption font-semibold text-sm" style={{ color: "var(--color-primary)" }}>{file?.name}</p>
                <p className="font-caption text-xs mt-1" style={{ color: "var(--color-muted-foreground)" }}>{fileSizeMB} MB</p>
              </div>
            ) : (
              <div>
                <p className="font-caption text-sm font-medium" style={{ color: "var(--color-foreground)" }}>Arrastre el archivo aquí o haga clic para seleccionar</p>
                <p className="font-caption text-xs mt-1" style={{ color: "var(--color-muted-foreground)" }}>ZIP, EXE, MSI, PKG, TAR.GZ, DEB, RPM, APK — máx. {MAX_SIZE_MB} MB</p>
              </div>
            )}
          </div>

          {(errors?.file || uploadError) && (
            <p className="text-xs font-caption flex items-center gap-1.5" style={{ color: "var(--color-error)" }}>
              <Icon name="AlertCircle" size={13} color="var(--color-error)" />
              {errors?.file || uploadError}
            </p>
          )}

          {uploading && (
            <div>
              <div className="flex justify-between mb-1">
                <span className="font-caption text-xs" style={{ color: "var(--color-muted-foreground)" }}>
                  {progress < 100 ? `Subiendo${estimatedSeconds ? ` (~${estimatedSeconds}s restantes)` : '...'}` : 'Completado'}
                </span>
                <span className="font-caption text-xs font-semibold" style={{ color: "var(--color-primary)" }}>{Math.round(progress)}%</span>
              </div>
              <div className="w-full h-2 rounded-full" style={{ backgroundColor: "var(--color-muted)" }}>
                <div
                  className="h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%`, background: "linear-gradient(90deg, var(--color-primary), var(--color-secondary))" }}
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Nombre del paquete" type="text" placeholder="Ej: SoftDist Core" value={form?.name} onChange={(e) => setForm({ ...form, name: e?.target?.value })} error={errors?.name} required disabled={uploading} />
            <Input label="Versión" type="text" placeholder="Ej: 2.4.1" value={form?.version} onChange={(e) => setForm({ ...form, version: e?.target?.value })} error={errors?.version} required disabled={uploading} />
          </div>

          <div>
            <label className="font-caption text-sm font-medium block mb-1" style={{ color: "var(--color-foreground)" }}>Notas de versión</label>
            <textarea
              rows={3}
              placeholder="Describa los cambios de esta versión..."
              value={form?.releaseNotes}
              onChange={(e) => setForm({ ...form, releaseNotes: e?.target?.value })}
              disabled={uploading}
              className="w-full rounded-md border px-3 py-2 text-sm font-caption resize-none focus:outline-none focus:ring-2"
              style={{ borderColor: "var(--color-border)", background: "var(--color-background)", color: "var(--color-foreground)" }}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Input
                label="TTL URL firmada (horas)"
                type="number"
                placeholder="24"
                value={form?.expirationHours}
                onChange={(e) => setForm({ ...form, expirationHours: e?.target?.value })}
                error={errors?.expirationHours}
                required
                disabled={uploading}
              />
              <p className="text-xs font-caption mt-1" style={{ color: "var(--color-muted-foreground)" }}>Desde configuración del sistema</p>
            </div>
            <div>
              <Input
                label="Límite de descargas"
                type="number"
                placeholder="0"
                value={form?.downloadLimit}
                onChange={(e) => setForm({ ...form, downloadLimit: e?.target?.value })}
                disabled={uploading}
              />
              <p className="text-xs font-caption mt-1" style={{ color: "var(--color-muted-foreground)" }}>0 = ilimitado</p>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t" style={{ borderColor: "var(--color-border)" }}>
          <Button variant="outline" onClick={onClose} disabled={uploading}>Cancelar</Button>
          <Button variant="default" onClick={handleSubmit} loading={uploading} iconName="Upload" iconPosition="left">
            {uploading ? `Subiendo ${Math.round(progress)}%` : 'Cargar Actualización'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UploadModal;